import React, { useEffect, useState } from 'react'
import { TrendingUp } from 'lucide-react'
import { useTeamAccess } from '../contexts/AuthContext'
import { EmptyState } from './ui/empty-state'
import { supabase } from '../lib/supabase'
import { formatEstimatedCost } from '../lib/modelPricing'
import {
  addToTotals,
  emptyTotals,
  fetchTeamAgentUsageDaily,
  hasEstimatedCost,
  hasUnpricedUsage,
  rowTotalTokens,
  sumUsage,
  type UsageTotals,
} from '../lib/agentUsage'
import { UsageCostFootnote, UsageCostText, LIST_PRICE_LABEL } from './UsageCost'
import { getModelColor } from '../lib/modelColors'
import { formatModelLabelShort } from '../lib/modelLabel'
import { CardContent, CardHeader, CardTitle, CardDescription } from './ui/card'
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar'
import { Skeleton } from './ui/skeleton'
import { rangeLabel, rangeLabelAsOf } from '../lib/dayLabel'

// ============================================================================
// Component: AgentsCostPanel
//
// Phase 3.5 Step 3 — admin-only "cost governance" leaderboard, mounted at the
// top of AgentsSection. Shows which humans are burning the most tokens via
// their agents.
//
// 2026-09-09: dropped the trailing-7-day window for the dashboard's date
// selector — took a `date` prop and filtered checkin_date to exactly that
// day, matching NeedsAttentionCard/PeopleAndAgentsPreview at the time. Not
// touched: TokenSpendOverTime, which stays a rolling trend chart by
// deliberate choice.
//
// 2026-09-17: takes the dashboard's full `range` ({from, to} date-key
// strings) instead of just its single end date — filters checkin_date with
// `.gte`/`.lte`, same change made to TopProjects/TopModelsUsed, so this
// leaderboard actually reflects a multi-day selection instead of quietly
// only ever showing the last day of it.
//
// Restyled 2026-09-05 to match the Figma design: a big estimated-total
// figure up top (summed across ALL of this team's activity in the window,
// not just the 10 rows actually rendered below it — the leaderboard list is
// still capped at 10 for layout, the header total is not), then a per-person
// list with an avatar, name, and "($amount)" instead of the previous
// "~$amount · N tokens" line. "Estimated" here means a list-price
// estimate priced server-side per usage event, not billed spend.
// Avatars are initials-only for now (AvatarFallback, same as
// PeopleAndAgentsPreview.tsx / NeedsAttentionCard.tsx elsewhere on this
// dashboard) — real Slack profile photos aren't wired up; see the 2026-09-05
// conversation note for what that would take.
//
// ROLE GATING — client-side via useTeamAccess (see AuthContext.tsx), admins
// and managers only. Plain members must see nothing: not a title, not an
// empty state, nothing.
//
// Updated 2026-09-29 for the three-tier permission model (see
// 20260928201832_add_pod_managers_table.sql): this used to be admin-only.
// The stale note that used to live here said the team-wide agent_checkin
// SELECT policy was still live alongside the admin-only one, making this
// role check "UI convenience, not the security boundary" -- that's no
// longer true. The team-wide policy was retired; `agent_checkin` now has
// exactly two SELECT policies, agent_checkin_select_admin (all rows) and
// agent_checkin_select_manager (only rows whose agent's owner is in a pod
// this manager manages, via private.can_read_agent_checkins_as_manager).
// Both are enforced in Postgres regardless of what this component does.
//
// That means this component doesn't need any client-side pod filtering of
// its own to be manager-scoped: RLS already narrows what `agent_checkin`
// returns per role, so a manager's query below naturally comes back
// containing only their managed pods' activity, and the aggregation from
// there is identical for admins and managers. This role check only decides
// whether the panel renders at all -- exactly the same "UI convenience"
// caveat as before, just no longer papering over a real gap underneath it.
//
// QUERY APPROACH — raw rows fetched client-side, aggregated in JS, not
// .rpc() or a PostgREST aggregate select. There's no existing RPC that
// computes this (checked: the only aggregate RPCs in this schema are
// standup-context functions, unrelated to agent_checkin), and creating one
// was explicitly out of scope for this step. This mirrors the exact
// client-side join/aggregate pattern AgentsSection.tsx already uses for its
// card grouping, so it's consistent with the rest of this feature rather
// than a new pattern.
//
// 2026-09-09: each row's bar is now SEGMENTED BY MODEL, same idea as
// TopProjects' per-project bars — width still represents this person's
// totalTokens relative to the leaderboard max (unchanged), but the filled
// portion is split into per-model slices sized by that model's share of
// this person's token volume. Colors come from src/lib/modelColors.ts's
// getModelColor, the same deterministic per-model color TokenSpendOverTime/
// TopModelsUsed/TopProjects use, so a model reads as the same color here
// too. Hand-rolled as a plain div bar rather than reusing ui/progress.tsx's
// <Progress> — Progress's Radix primitive only supports one indicator fill,
// no way to render multiple colored children inside it — matching the same
// choice TopProjects already made for the same reason. A shared legend
// below the list (not per-row) maps color -> model, same pattern as
// TopProjects' footer legend.
//
// 2026-10-02 (Step 6 of docs/TOKEN_COST_ACCURACY_PLAN.md): tokens, $ and the
// per-model slices now come from get_team_agent_usage_daily (server-priced,
// cache tokens included) via src/lib/agentUsage.ts. The RPC itself enforces
// the same admin/manager visibility as agent_checkin's RLS: usage of agents
// the caller can't see in detail arrives with agent_id NULL, pre-aggregated.
// Those rows can't be attributed to a person, so the per-person list skips
// them, but the header grand total still counts them (it's a team figure).
// Task counts and "last activity" still come from agent_checkin (one row per
// reported task), which admins and managers can read.
// ============================================================================

interface AgentsCostPanelProps {
  teamId: string
  range: { from: string; to: string } // YYYY-MM-DD, the dashboard's selected range
  // Pod filter (NewDashboard.tsx's pod dropdown). Safe here regardless of
  // role since this widget is already admin/manager-gated below, so the
  // caller always has real (unmasked) agent_id visibility.
  agentIds?: string[]
}

interface ModelSlice {
  model: string
  tokens: number
}

interface LeaderboardRow {
  userId: string
  displayName: string
  avatarUrl: string | null
  totalTokens: number
  taskCount: number
  lastActivity: string | null
  // Server-priced usage totals for this person (costUsd null = nothing
  // priceable, distinct from a confirmed $0).
  usage: UsageTotals
  // This person's token volume broken down by model, largest first — drives
  // the per-row segmented bar fill.
  modelSlices: ModelSlice[]
}

const formatDaysAgo = (isoDate: string | null): string => {
  if (!isoDate) return 'no activity'
  const then = new Date(isoDate).getTime()
  const now = Date.now()
  const days = Math.floor((now - then) / (1000 * 60 * 60 * 24))
  if (days <= 0) return 'today'
  if (days === 1) return '1 day ago'
  return `${days} days ago`
}

const AgentsCostPanel: React.FC<AgentsCostPanelProps> = ({ teamId, range, agentIds }) => {
  const { role, loading: roleLoading } = useTeamAccess(teamId)
  const canViewCosts = role === 'owner' || role === 'admin' || role === 'manager'

  const [rows, setRows] = useState<LeaderboardRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Grand total across every user with activity in the window, not just the
  // top 10 rendered below — kept separate from `rows` so slicing the list
  // for display never quietly shrinks the header number too.
  const [grandTotals, setGrandTotals] = useState<UsageTotals>(emptyTotals())

  useEffect(() => {
    if (roleLoading) return

    if (!canViewCosts) {
      setLoading(false)
      return
    }

    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)

      // checkin_date / usage_date (the day being reported), not created_at —
      // so this agrees with the dashboard's range selector the same way
      // NeedsAttentionCard/PeopleAndAgentsPreview do. created_at is still
      // used below for the human-readable "last activity" recency display.
      //
      // agent_checkin / agents_team_visible postdate the last generation of
      // src/types/supabase.ts (see AgentsSection.tsx for the same note).
      // Cast at the `.from()` boundary only.
      //
      // Pod filter: narrows both the task-count source (agent_checkin) and
      // the usage/cost source (the RPC, via opts.agentIds) to the selected
      // pod's agents, so the leaderboard and the usage figures it shows stay
      // consistent with each other. An empty (but non-null) agentIds means
      // "pod has no agents" -- short-circuited below rather than sent as
      // `.in('agent_id', [])`, which is an edge case worth avoiding rather
      // than relying on either library to handle it the way we want.
      if (agentIds && agentIds.length === 0) {
        setRows([])
        setGrandTotals(emptyTotals())
        setLoading(false)
        return
      }

      let checkinsQuery = (supabase.from as any)('agent_checkin')
        .select('agent_id, created_at')
        .is('deleted_at', null)
        .gte('checkin_date', range.from)
        .lte('checkin_date', range.to)
      if (agentIds) checkinsQuery = checkinsQuery.in('agent_id', agentIds)

      const [checkinsResult, agentsResult, usersResult, usageResult] = await Promise.all([
        checkinsQuery,
        (supabase.from as any)('agents_team_visible').select('id, user_id').eq('team_id', teamId),
        (supabase.from as any)('users').select('id, name, email, avatar_url').eq('team_id', teamId),
        fetchTeamAgentUsageDaily(teamId, range.from, range.to, agentIds ? { agentIds } : undefined).then(
          (value) => ({ value, error: null as unknown }),
          (error: unknown) => ({ value: null, error })
        ),
      ])

      if (cancelled) return

      if (checkinsResult.error || agentsResult.error || usersResult.error || usageResult.error) {
        console.error(
          'Failed to load token leaderboard:',
          checkinsResult.error ?? agentsResult.error ?? usersResult.error ?? usageResult.error
        )
        setError('Could not load token leaderboard.')
        setLoading(false)
        return
      }
      const usageRows = usageResult.value!

      const agentToUser = new Map<string, string>(
        ((agentsResult.data ?? []) as { id: string; user_id: string }[]).map((a) => [a.id, a.user_id])
      )
      const usersById = new Map<
        string,
        { name: string | null; email: string | null; avatar_url: string | null }
      >(
        (
          (usersResult.data ?? []) as {
            id: string
            name: string | null
            email: string | null
            avatar_url: string | null
          }[]
        ).map((u) => [u.id, { name: u.name, email: u.email, avatar_url: u.avatar_url }])
      )

      const byUser = new Map<
        string,
        {
          taskCount: number
          lastActivity: string | null
          usage: UsageTotals
          tokensByModel: Map<string, number>
        }
      >()
      const entryFor = (userId: string) => {
        let existing = byUser.get(userId)
        if (!existing) {
          existing = {
            taskCount: 0,
            lastActivity: null,
            usage: emptyTotals(),
            tokensByModel: new Map<string, number>(),
          }
          byUser.set(userId, existing)
        }
        return existing
      }

      // agent_checkin is already RLS-scoped per role (admin: all, manager:
      // managed pods), so no extra filtering here.
      for (const row of (checkinsResult.data ?? []) as { agent_id: string; created_at: string }[]) {
        const userId = agentToUser.get(row.agent_id)
        if (!userId) continue
        const existing = entryFor(userId)
        existing.taskCount += 1
        if (!existing.lastActivity || row.created_at > existing.lastActivity) {
          existing.lastActivity = row.created_at
        }
      }

      for (const row of usageRows) {
        // agent_id null = an agent this caller can't see in detail; it only
        // counts toward the grand total below, never toward a person.
        if (!row.agentId) continue
        const userId = agentToUser.get(row.agentId)
        if (!userId) continue
        const existing = entryFor(userId)
        addToTotals(existing.usage, row)
        // "unknown" (lowercase) — same bucket key TopModelsUsed/
        // TokenSpendOverTime/TopProjects use, so getModelColor("unknown")
        // resolves to the same color everywhere an unpriceable model shows up.
        const modelKey = row.model || 'unknown'
        existing.tokensByModel.set(modelKey, (existing.tokensByModel.get(modelKey) ?? 0) + rowTotalTokens(row))
      }

      const allUsers = Array.from(byUser.entries())

      const leaderboard: LeaderboardRow[] = allUsers
        .map(([userId, agg]) => {
          const user = usersById.get(userId)
          const displayName = user?.name || user?.email?.split('@')[0] || 'Unknown user'
          return {
            userId,
            displayName,
            avatarUrl: user?.avatar_url ?? null,
            totalTokens: agg.usage.totalTokens,
            taskCount: agg.taskCount,
            lastActivity: agg.lastActivity,
            usage: agg.usage,
            modelSlices: Array.from(agg.tokensByModel.entries())
              .map(([model, tokens]) => ({ model, tokens }))
              .sort((a, b) => b.tokens - a.tokens),
          }
        })
        .sort((a, b) => b.totalTokens - a.totalTokens)
        .slice(0, 10)

      setRows(leaderboard)
      // Grand total over every usage row the RPC returned -- including
      // agents whose owner is no longer on the team and null-agent rows the
      // caller can't see per-agent -- so the header never under-reports.
      setGrandTotals(sumUsage(usageRows))
      setLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [teamId, canViewCosts, roleLoading, range.from, range.to, agentIds?.join(",")])

  // Plain members (and the still-resolving role check) see nothing at all —
  // no title, no empty state, no loading text.
  if (roleLoading || !canViewCosts) {
    return null
  }

  // Stacked list, not a table — a 4-column table breaks down and wraps
  // badly once squeezed into a narrow lg:col-span-1 grid cell on Dashboard.
  const max = Math.max(...rows.map((r) => r.totalTokens), 1)

  // Legend covers every model appearing across the visible rows, largest
  // total-token consumer first — same idea as TopProjects' footer legend,
  // labeled with the short "Opus 4.5"-style form (no "Claude" prefix).
  const legendTokensByModel = new Map<string, number>()
  for (const row of rows) {
    for (const slice of row.modelSlices) {
      legendTokensByModel.set(slice.model, (legendTokensByModel.get(slice.model) ?? 0) + slice.tokens)
    }
  }
  const legend = Array.from(legendTokensByModel.keys()).sort(
    (a, b) => (legendTokensByModel.get(b) ?? 0) - (legendTokensByModel.get(a) ?? 0)
  )

  return (
    <div className="flex h-full flex-col">
      <CardHeader>
        <CardTitle className="text-3xl font-bold tracking-tight text-foreground">
          {grandTotals.costUsd === null && grandTotals.eventCount > 0 ? (
            <UsageCostText totals={grandTotals} prefix="" />
          ) : (
            <>
              {formatEstimatedCost(grandTotals.costUsd ?? 0)}
              {hasEstimatedCost(grandTotals) && '*'}
              {hasUnpricedUsage(grandTotals) && '+'}
            </>
          )}
        </CardTitle>
        <CardDescription>
          Connector token usage ({LIST_PRICE_LABEL.toLowerCase()}) — {rangeLabel(range.from, range.to)}
        </CardDescription>
      </CardHeader>
      <div className="border-t border-border" />
      <CardContent className="flex flex-1 flex-col p-0">
        {loading ? (
          <div className="divide-y divide-border">
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-2 px-6 py-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                  <Skeleton className="h-4 w-16 shrink-0" />
                </div>
                <Skeleton className="h-4 w-full rounded-full" />
                <Skeleton className="h-3 w-24" />
              </div>
            ))}
          </div>
        ) : error ? (
          <p className="px-6 py-6 text-sm text-destructive">{error}</p>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={TrendingUp}
            message={
              grandTotals.eventCount > 0
                ? `No per-person activity you can view ${rangeLabelAsOf(range.from, range.to)}.`
                : `No connector activity ${rangeLabelAsOf(range.from, range.to)}.`
            }
          />
        ) : (
          <div className="divide-y divide-border">
            {rows.map((row) => (
              <div key={row.userId} className="space-y-2 px-6 py-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar className="h-8 w-8 shrink-0">
                      {row.avatarUrl && <AvatarImage src={row.avatarUrl} alt="" />}
                      <AvatarFallback>{row.displayName.charAt(0).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span className="truncate text-sm font-medium text-foreground">{row.displayName}</span>
                  </div>
                  <span className="shrink-0 text-sm text-muted-foreground">
                    <UsageCostText totals={row.usage} prefix="" />
                  </span>
                </div>
                <div className="relative h-4 w-full overflow-hidden rounded-full bg-foreground/10">
                  <div
                    className="flex h-full"
                    style={{ width: `${(row.totalTokens / max) * 100}%` }}
                  >
                    {row.modelSlices.map((slice) => (
                      <div
                        key={slice.model}
                        className="h-full"
                        style={{
                          width: `${(slice.tokens / (row.totalTokens || 1)) * 100}%`,
                          backgroundColor: getModelColor(slice.model),
                        }}
                      />
                    ))}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {row.taskCount} {row.taskCount === 1 ? 'task' : 'tasks'} · {formatDaysAgo(row.lastActivity)}
                </p>
              </div>
            ))}
          </div>
        )}
        {!loading && !error && legend.length > 0 && (
          <div className="flex flex-wrap gap-x-3 gap-y-1.5 border-t border-border px-6 py-3">
            {legend.map((model) => (
              <div key={model} className="flex items-center gap-1.5">
                <span className="h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: getModelColor(model) }} />
                <span className="truncate text-xs text-muted-foreground">
                  {model === 'unknown' ? 'Unknown model' : formatModelLabelShort(model)}
                </span>
              </div>
            ))}
          </div>
        )}
        {!loading && !error && grandTotals.eventCount > 0 && (
          <UsageCostFootnote totals={grandTotals} className="px-6 pb-4" />
        )}
      </CardContent>
    </div>
  )
}

export default AgentsCostPanel
